import { ref, nextTick } from 'vue';

/**
 * Composable for handling client creation functionality
 * Provides reusable methods for opening CreateClient modal and handling events
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onClientCreated - Callback when client is created
 * @param {Function} options.onClientClosed - Optional callback when modal is closed
 * @returns {Object} Client creation methods and reactive state
 */
export function useClientCreation(options = {}) {
  const {
    onClientCreated = () => {},
    onClientClosed = () => {}
  } = options;

  // Reactive state
  const showCreateClient = ref(false);
  const createClientRef = ref(null);

  /**
   * Opens the client creation modal
   * @param {string} typedClientName - Optional pre-filled client name
   */
  const addNewClient = (typedClientName = '', method = '') => {
    showCreateClient.value = true;
    nextTick(() => {
      if (createClientRef.value && createClientRef.value.openDialog) {
        createClientRef.value.openDialog(typedClientName, method);
      }
    });
  };

  /**
   * Handles successful client creation
   * @param {Object} newClient - The newly created client object
   */
  const handleClientCreated = newClient => {
    showCreateClient.value = false;
    if (newClient) {
      // Call the provided callback with the new client
      onClientCreated(newClient);
    }
  };

  /**
   * Handles modal close (cancel/escape)
   */
  const handleClientClosed = () => {
    showCreateClient.value = false;
    // Call the optional close callback
    onClientClosed();
  };
  return {
    // Reactive state
    showCreateClient,
    createClientRef,
    // Methods
    addNewClient,
    handleClientCreated,
    handleClientClosed
  };
}